import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useState } from 'react';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import GenerateCreateField from './GenerateCreateField';
import dayjs from 'dayjs';
import { useNotify } from 'src/context/ContextNotify';
import { postAPI } from 'src/utils/api';
import StackRow from './StackRow';
import { isEmpty } from 'src/common/helper';
import { getToken } from 'src/utils/auth';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px', height: '32px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  flex: 1
};

type DialogFormType = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: () => void;
};

const DialogCreate: React.FC<DialogFormType> = (props) => {
  const notifyContext = useNotify();
  // const [answerDetail, setAnswerDetail] = useState<any>(null);
  const [questions, setQuestions] = useState<any>([]);
  const [objectQuestion, setObjectQuestion] = useState({
    title: '',
    description: '',
  });

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setObjectQuestion((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    let keyError = '';
    if (isEmpty(objectQuestion.title)) {
      notifyContext.setNotifyMessage({
        show: true,
        message: `タイトルを追加してください`,
        type: 'error',
      });
      return false;
    }
    if (isEmpty(objectQuestion.description)) {
      notifyContext.setNotifyMessage({
        show: true,
        message: `説明を追加してください`,
        type: 'error',
      });
      return false;
    }
    if (questions.length === 0) {
      notifyContext.setNotifyMessage({
        show: true,
        message: `質問を追加してください`,
        type: 'error',
      });
      return false;
    }
    questions.map((question: any) => {
      if (!question.label) {
        keyError = `質問を入力してください`;
      }
      switch (question.type) {
        case 'date':
          if (
            (question.min_date &&
              !dayjs(question.min_date).isValid()) ||
            (question.max_date &&
              !dayjs(question.max_date).isValid())
          ) {
            keyError = `日付を入力してください`;
          }
          if (question.min_date && question.max_date && dayjs(question.min_date).isValid() && dayjs(question.max_date).isValid() && dayjs(question.min_date).isAfter(dayjs(question.max_date))) {
            keyError = `最小日付は最大日付より小さくなければなりません`;
          }
          break;
        case 'time':
          if (
            (question.min_time &&
              !dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.min_time}`).isValid()) ||
            (question.max_time &&
              !dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.max_time}`).isValid())
          ) {
            keyError = `時間を入力してください`;
          }
          if (question.min_time && question.max_time && dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.min_time}`).isValid() && dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.max_time}`).isValid() && dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.min_time}`).isAfter(dayjs(`${dayjs().format('YYYY-MM-DD')} ${question.max_time}`))) {
            keyError = `最小時間は最大時間より小さくなければなりません`;
          }
          break;
        default:
          break;
      }
      return question;
    });
    if (keyError) {
      notifyContext.setNotifyMessage({
        show: true,
        message: `${keyError}`,
        type: 'error',
      });
      return false;
    }

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const handleSubmit = () => {
    try {
      if (!validate()) {
        return;
      }

      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const fields = questions.reduce(
        (temp: any, current: any, index: number) => {
          current['order'] = index + 1;
          current['field_id'] = `q${index + 1}`;
          temp.push(current);
          return temp;
        },
        []
      );

      const data = {
        title: objectQuestion.title,
        description: objectQuestion.description,
        fields: [...fields],
        user_id: 1,
      };

      postAPI(`/staff/questionnaires?token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            setObjectQuestion({
              title: '',
              description: '',
            });
            setQuestions([]);
            props.handleConfirm();
          } else {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg || 'Failed to post data',
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleAddNewClick = () => {
    let tempQues = [...questions];
    const question = {
      type: 'text',
      label: '',
      help_text: '',
      field_id: '',
      min_length: '',
      max_length: '',
      required: false,
      order: 1
    };
    tempQues.push(question);
    setQuestions(tempQues);
  };

  const handleChangeValue = (value: string, position: number) => {
    let tempQues = [...questions];
    tempQues[position] = value;
    setQuestions(tempQues);
  };

  const handleClose = () => {
    setQuestions([]);
    props.handleClose();
  };

  const removeQuestion = (position: number) => {
    let tempQues = [...questions];
    tempQues.splice(position, 1);
    setQuestions(tempQues);
  };

  const handleModified = (order: number) => {};

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='scroll-dialog-title'>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Typography fontSize={'20px'} fontWeight={500}>
            問診表
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ width: '500px' }}>
        <Stack alignItems={'center'}>
          <StackRow>
            <Typography width={'120px'}>タイトル</Typography>
            <OutlinedInput
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'title'}
              value={objectQuestion.title}
              onChange={(event) => handleInputChange(event)}
            />
          </StackRow>
          <StackRow>
            <Typography width={'120px'}>説明</Typography>
            <OutlinedInput
              sx={inputStyle}
              inputProps={{
                sx: {
                  backgroundColor: 'primary.light',
                },
              }}
              name={'description'}
              value={objectQuestion.description}
              onChange={(event) => handleInputChange(event)}
            />
          </StackRow>
          <Divider sx={{ my: '8px', width: '100%' }} />
          {questions.map((question: any, index: number) => {
            return (
              <GenerateCreateField
                field={question}
                key={index}
                position={index}
                handleChangeValue={handleChangeValue}
                removeQuestion={removeQuestion}
                handleModified={handleModified}
              />
            );
          })}
        </Stack>
        <BtnGradient
          text='質問を追加'
          width='120px'
          handleButtonClick={handleAddNewClick}
        />
      </DialogContent>
      <DialogActions sx={{ p: '20px' }}>
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} />
        <BtnGradient text='登録' handleButtonClick={handleSubmit} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogCreate;
